/**
 * DemoConversationContext
 *
 * Holds a single persistent conversation that accumulates messages
 * across all steps of the guided demo flow. Both the full ChatPage
 * (chat steps) and the assistant side-panel (view steps) render
 * from this same message list.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export interface DemoMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface DemoConversationState {
  messages: DemoMessage[];
  /** Append one or more messages to the conversation */
  addMessages: (msgs: DemoMessage[]) => void;
  /** Whether the chat is currently playing scripted messages */
  isPlaying: boolean;
  setIsPlaying: (v: boolean) => void;
  /** Clear the conversation (e.g. on restart) */
  clear: () => void;
}

const DemoConversationCtx = createContext<DemoConversationState | null>(null);

export const DemoConversationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);

  const addMessages = useCallback((msgs: DemoMessage[]) => {
    setMessages(prev => [...prev, ...msgs]);
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
  }, []);

  return (
    <DemoConversationCtx.Provider value={{ messages, addMessages, isPlaying, setIsPlaying, clear }}>
      {children}
    </DemoConversationCtx.Provider>
  );
};

export function useDemoConversation() {
  const ctx = useContext(DemoConversationCtx);
  if (!ctx) {
    // Return a no-op fallback when outside the provider (non-demo pages)
    return {
      messages: [] as DemoMessage[],
      addMessages: () => {},
      isPlaying: false,
      setIsPlaying: () => {},
      clear: () => {},
    };
  }
  return ctx;
}
